import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
  alpha,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import {
  portalCreateTaskAttachment,
  portalCreateTaskCard,
  portalCreateTaskComment,
  portalDeleteTaskAttachment,
  portalGetTasksBoard,
  portalMoveTaskCard,
  portalUpdateTaskCard,
} from "../../api/clientPortal";
import { portalUploadOne, inferPortalFileMessageType } from "../../api/portalUpload";
import { getInitials } from "../../utils/utils";
import { getClientPortalErrorMessage } from "./clientPortalErrorMessage";
import FileDropHandler from "../../components/MessageHistory/FileDropHandler";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

function groupCardsByColumn(columns, cards) {
  const byCol = new Map();
  for (const col of columns || []) byCol.set(Number(col.id), []);
  for (const card of cards || []) {
    const key = Number(card.columnId);
    if (!byCol.has(key)) byCol.set(key, []);
    byCol.get(key).push(card);
  }
  for (const list of byCol.values()) {
    list.sort((a, b) => (a.position || 0) - (b.position || 0) || a.id - b.id);
  }
  return byCol;
}

function previewText(raw, max = 96) {
  const text = String(raw || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return null;
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function isOverdue(dueAt) {
  if (!dueAt) return false;
  const d = new Date(dueAt);
  return !Number.isNaN(d.getTime()) && d < new Date();
}

function formatDueLabel(dueAt) {
  if (!dueAt) return null;
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR", { day: "numeric", month: "short" });
}

function formatDueFull(dueAt) {
  if (!dueAt) return null;
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toDatetimeLocalValue(dueAt) {
  if (!dueAt) return "";
  const raw = String(dueAt).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return raw.slice(0, 16);
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw)) return raw.replace(" ", "T").slice(0, 16);
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

function formatCommentTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function attachmentKind(att) {
  const mime = String(att?.mimeType || "").toLowerCase();
  const name = String(att?.fileName || att?.fileUrl || "");
  if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return "image";
  if (mime.startsWith("video/") || /\.(mp4|webm|mov|m4v)$/i.test(name)) return "video";
  if (mime.startsWith("audio/") || /\.(mp3|wav|m4a|aac)$/i.test(name)) return "audio";
  if (mime.includes("pdf") || /\.pdf$/i.test(name)) return "pdf";
  return "file";
}

function columnAccent(theme, col) {
  if (col?.isDone) return theme.palette.success.main;
  if (col?.isWaiting) return theme.palette.warning.main;
  return theme.palette.primary.main;
}

function columnSurface(theme, col) {
  const base = alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.45 : 0.82);
  if (col?.isDone) {
    return `linear-gradient(180deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${base} 72px)`;
  }
  if (col?.isWaiting) {
    return `linear-gradient(180deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${base} 72px)`;
  }
  return base;
}

function AssigneeAvatars({ users = [], size = 22 }) {
  if (!users.length) return null;
  return (
    <AvatarGroup
      max={3}
      sx={{
        "& .MuiAvatar-root": {
          width: size,
          height: size,
          fontSize: size > 24 ? 12 : 10,
          borderWidth: 1.5,
          fontWeight: 700,
        },
      }}
    >
      {users.map((u) => (
        <Tooltip key={`${u.type || "user"}:${u.id}`} title={u.name || "Equipe"}>
          <Avatar src={u.avatarUrl || undefined} alt={u.name || ""}>
            {getInitials(u.name || "?")}
          </Avatar>
        </Tooltip>
      ))}
    </AvatarGroup>
  );
}

function PortalAttachmentTile({ att, onDelete, deleting }) {
  const theme = useTheme();
  const kind = attachmentKind(att);
  const open = () => {
    if (att.fileUrl) window.open(att.fileUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        opacity: deleting ? 0.55 : 1,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        sx={{ cursor: "pointer", textAlign: "left" }}
      >
        {kind === "image" ? (
          <Box
            component="img"
            src={att.fileUrl}
            alt={att.fileName || ""}
            sx={{
              width: "100%",
              height: 120,
              objectFit: "cover",
              display: "block",
              bgcolor: "action.hover",
            }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={0.75}
            sx={{ height: 120, bgcolor: alpha(theme.palette.primary.main, 0.04), px: 1 }}
          >
            {kind === "pdf" ? (
              <PictureAsPdfOutlinedIcon color="error" />
            ) : (
              <InsertDriveFileOutlinedIcon color="action" />
            )}
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: "100%" }}>
              {att.fileName}
            </Typography>
          </Stack>
        )}
        <Box sx={{ px: 1.1, py: 0.85 }}>
          <Typography variant="caption" fontWeight={700} noWrap display="block">
            {att.fileName}
          </Typography>
          {att.sizeBytes != null ? (
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(att.sizeBytes)}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {onDelete ? (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(att.id);
          }}
          disabled={deleting}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            "&:hover": { bgcolor: "background.paper" },
          }}
          aria-label="Remover anexo"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Paper>
  );
}

function PortalTaskCard({ card, column, onOpen, dragHandleProps, isDragging }) {
  const theme = useTheme();
  const inDone = Boolean(column?.isDone);
  const overdue = !inDone && isOverdue(card.dueAt);
  const dueLabel = formatDueLabel(card.dueAt);
  const description = previewText(card.description);
  const commentCount = (card.comments || []).length;
  const attachmentCount = (card.attachments || []).length;
  const assignees = card.assigneeUsers || [];
  const accent = inDone
    ? theme.palette.success.main
    : overdue
      ? theme.palette.error.main
      : card.hasInternalEscalation
        ? theme.palette.info.main
        : columnAccent(theme, column);

  return (
    <Paper
      elevation={0}
      {...(dragHandleProps || {})}
      onClick={() => onOpen(card)}
      sx={{
        position: "relative",
        p: 1.35,
        pl: 1.75,
        borderRadius: 2,
        cursor: isDragging ? "grabbing" : "grab",
        bgcolor: inDone
          ? alpha(theme.palette.grey[50], theme.palette.mode === "dark" ? 0.08 : 0.98)
          : "background.paper",
        border: "1px solid",
        borderColor: isDragging ? alpha(accent, 0.5) : alpha(theme.palette.divider, 0.9),
        boxShadow: isDragging
          ? `0 10px 28px ${alpha("#0f172a", 0.18)}`
          : theme.palette.mode === "dark"
            ? "none"
            : `0 1px 2px ${alpha("#0f172a", 0.04)}, 0 4px 12px ${alpha("#0f172a", 0.04)}`,
        transition: isDragging
          ? "none"
          : "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
        "&:hover": {
          borderColor: alpha(accent, 0.45),
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 0 0 1px ${alpha(accent, 0.25)}`
              : `0 2px 8px ${alpha(accent, 0.14)}`,
          transform: isDragging ? "none" : "translateY(-1px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 10,
          bottom: 10,
          width: 3,
          borderRadius: "0 3px 3px 0",
          bgcolor: accent,
        },
      }}
    >
      {(card.hasInternalEscalation || overdue) && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.85 }}>
          {card.hasInternalEscalation ? (
            <Chip
              size="small"
              icon={<SyncAltIcon sx={{ fontSize: "14px !important" }} />}
              label="Em tratamento"
              color="info"
              sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
            />
          ) : null}
          {overdue ? (
            <Chip
              size="small"
              icon={<ScheduleOutlinedIcon sx={{ fontSize: "14px !important" }} />}
              label="Prazo vencido"
              color="error"
              sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
            />
          ) : null}
        </Stack>
      )}

      <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.35, letterSpacing: -0.1 }}>
        <Box component="span" sx={{ color: "text.secondary", fontWeight: 700, mr: 0.5 }}>
          #{card.id}
        </Box>
        {card.title}
      </Typography>

      {description ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.7, lineHeight: 1.45 }}
        >
          {description}
        </Typography>
      ) : null}

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 1.1 }}
      >
        <Stack direction="row" spacing={0.65} alignItems="center" flexWrap="wrap" useFlexGap>
          {dueLabel ? (
            <Chip
              size="small"
              icon={<ScheduleOutlinedIcon sx={{ fontSize: "14px !important" }} />}
              label={dueLabel}
              color={overdue ? "error" : "default"}
              variant={overdue ? "filled" : "outlined"}
              sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
            />
          ) : null}
          {commentCount > 0 ? (
            <Chip
              size="small"
              icon={<ChatBubbleOutlineIcon sx={{ fontSize: "13px !important" }} />}
              label={String(commentCount)}
              variant="outlined"
              sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
            />
          ) : null}
          {attachmentCount > 0 ? (
            <Chip
              size="small"
              icon={<AttachFileOutlinedIcon sx={{ fontSize: "13px !important" }} />}
              label={String(attachmentCount)}
              variant="outlined"
              sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
            />
          ) : null}
          {inDone ? (
            <Chip
              size="small"
              icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
              label="Concluída"
              color="success"
              variant="outlined"
              sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
            />
          ) : null}
        </Stack>
        <AssigneeAvatars users={assignees} size={22} />
      </Stack>
    </Paper>
  );
}

function PortalTaskColumn({ column, cards, onOpenCard, onQuickAdd, canAdd, canDrop }) {
  const theme = useTheme();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const accent = columnAccent(theme, column);
  const dropEnabled = canDrop !== false;

  const submit = async () => {
    const title = draft.trim();
    if (!title || submitting) return;
    setSubmitting(true);
    try {
      const ok = await onQuickAdd?.(column.id, title);
      if (ok !== false) {
        setDraft("");
        setAdding(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: { xs: "min(86vw, 300px)", sm: 300, md: 312 },
        flexShrink: 0,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.85),
        background: columnSurface(theme, column),
        display: "flex",
        flexDirection: "column",
        maxHeight: { xs: "none", md: "calc(100vh - 200px)" },
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.35, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: accent,
              flexShrink: 0,
            }}
          />
          <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
            {column.name}
          </Typography>
          <Chip
            size="small"
            label={cards.length}
            sx={{
              height: 22,
              minWidth: 28,
              fontWeight: 800,
              fontSize: "0.7rem",
              bgcolor: alpha(accent, 0.12),
              color: accent,
            }}
          />
        </Stack>
        {column.isWaiting ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, pl: 2 }}>
            Aguardando sua resposta ou validação
          </Typography>
        ) : null}
        {column.isDone ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, pl: 2 }}>
            Finalizadas pela equipe
          </Typography>
        ) : null}
      </Box>

      <Droppable droppableId={String(column.id)} type="CARD" isDropDisabled={!dropEnabled}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              px: 1.25,
              pb: 1.25,
              flex: 1,
              minHeight: 96,
              overflowY: "auto",
              borderRadius: 2,
              bgcolor: snapshot.isDraggingOver
                ? alpha(theme.palette.primary.main, 0.07)
                : "transparent",
              outline: snapshot.isDraggingOver
                ? `2px dashed ${alpha(theme.palette.primary.main, 0.35)}`
                : "2px dashed transparent",
              outlineOffset: -2,
              transition: "background-color 0.2s ease, outline-color 0.2s ease",
            }}
          >
            {cards.map((card, index) => (
              <Draggable key={String(card.id)} draggableId={String(card.id)} index={index}>
                {(dragProvided, dragSnapshot) => (
                  <Box
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    sx={{
                      mb: 1,
                      opacity: dragSnapshot.isDragging ? 0.98 : 1,
                      transform: dragSnapshot.isDragging ? "rotate(1.5deg) scale(1.02)" : undefined,
                      filter: dragSnapshot.isDragging
                        ? "drop-shadow(0 8px 16px rgba(15,23,42,0.18))"
                        : "none",
                    }}
                  >
                    <PortalTaskCard
                      card={card}
                      column={column}
                      onOpen={onOpenCard}
                      dragHandleProps={dragProvided.dragHandleProps}
                      isDragging={dragSnapshot.isDragging}
                    />
                  </Box>
                )}
              </Draggable>
            ))}
            {provided.placeholder}

            {cards.length === 0 ? (
              <Box
                sx={{
                  py: 2.5,
                  px: 1.5,
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "divider",
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                  {column.isDone
                    ? "As tarefas concluídas aparecerão aqui."
                    : dropEnabled
                      ? "Arraste tarefas para cá ou crie uma nova abaixo."
                      : "A conclusão fica com a equipe."}
                </Typography>
              </Box>
            ) : null}
          </Box>
        )}
      </Droppable>

      {canAdd ? (
        <Box sx={{ px: 1.25, pb: 1.25 }}>
          {adding ? (
            <Stack spacing={1}>
              <TextField
                size="small"
                autoFocus
                fullWidth
                placeholder="Resumo da tarefa…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                  if (e.key === "Escape") {
                    setAdding(false);
                    setDraft("");
                  }
                }}
                disabled={submitting}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={submit}
                  disabled={submitting || !draft.trim()}
                >
                  {submitting ? "Criando…" : "Adicionar"}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setAdding(false);
                    setDraft("");
                  }}
                  disabled={submitting}
                >
                  Cancelar
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Button
              fullWidth
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAdding(true)}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                border: "1px dashed",
                borderColor: "divider",
                color: "text.secondary",
                py: 1,
                "&:hover": {
                  borderColor: "primary.main",
                  color: "primary.main",
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              Adicionar tarefa
            </Button>
          )}
        </Box>
      ) : null}
    </Paper>
  );
}

function PendingFilesList({ files, onRemove }) {
  if (!files.length) return null;
  return (
    <Stack spacing={0.75}>
      {files.map((file, index) => (
        <Stack
          key={`${file.name}-${index}`}
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            px: 1,
            py: 0.65,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <AttachFileOutlinedIcon fontSize="small" color="action" />
          <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {file.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(file.size)}
          </Typography>
          <IconButton size="small" onClick={() => onRemove(index)} aria-label="Remover arquivo">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
    </Stack>
  );
}

function PortalCreateDialog({ open, onClose, onCreate, saving, defaultColumnId }) {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setDescription("");
      setDueAt("");
      setFiles([]);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        Nova tarefa
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
          Descreva o que precisa, anexe arquivos se quiser e defina um prazo desejado.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="O que precisa ser feito?"
            placeholder="Ex.: Revisar relatório mensal"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            autoFocus
            required
          />
          <TextField
            label="Detalhes (opcional)"
            placeholder="Contexto, links ou o que a equipe precisa saber"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            label="Prazo desejado (opcional)"
            type="datetime-local"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Ajuda a equipe a priorizar. Você pode ajustar depois."
          />

          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <AttachFileOutlinedIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={800}>
                Anexos
              </Typography>
            </Stack>
            <FileDropHandler
              onFileUpload={(incoming) => {
                const list = Array.isArray(incoming) ? incoming : [incoming];
                setFiles((prev) => [...prev, ...list.filter(Boolean)]);
              }}
              dropLabel="Solte arquivos para anexar"
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderStyle: "dashed",
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <CloudUploadOutlinedIcon color="action" />
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        Arraste arquivos aqui
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ou clique para selecionar do seu computador
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Adicionar anexo
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    onChange={(e) => {
                      const selected = Array.from(e.target.files || []);
                      if (selected.length) setFiles((prev) => [...prev, ...selected]);
                      e.target.value = "";
                    }}
                  />
                </Stack>
              </Paper>
            </FileDropHandler>
            <Box sx={{ mt: 1 }}>
              <PendingFilesList
                files={files}
                onRemove={(index) => setFiles((prev) => prev.filter((_, i) => i !== index))}
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            onCreate({
              title: title.trim(),
              description: description.trim() || null,
              dueAt: dueAt || null,
              columnId: defaultColumnId,
              files,
            })
          }
          disabled={saving || !title.trim()}
        >
          {saving ? "Criando…" : "Criar tarefa"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CommentMedia({ comment }) {
  if (!comment?.fileUrl) return null;
  const kind = attachmentKind({
    fileName: comment.fileName,
    fileUrl: comment.fileUrl,
    mimeType: comment.mimeType,
  });
  if (kind === "image") {
    return (
      <Box
        component="a"
        href={comment.fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: "block", mt: 1 }}
      >
        <Box
          component="img"
          src={comment.fileUrl}
          alt={comment.fileName || "imagem"}
          sx={{
            maxWidth: "100%",
            maxHeight: 220,
            borderRadius: 1.5,
            display: "block",
          }}
        />
      </Box>
    );
  }
  return (
    <Button
      size="small"
      href={comment.fileUrl}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={
        kind === "pdf" ? <PictureAsPdfOutlinedIcon /> : <InsertDriveFileOutlinedIcon />
      }
      sx={{ mt: 1, textTransform: "none", fontWeight: 700, justifyContent: "flex-start" }}
    >
      {comment.fileName || "Abrir anexo"}
    </Button>
  );
}

function buildPortalAssigneeOptions(assigneeOptions) {
  const list = [];
  if (assigneeOptions?.workspace) {
    list.push({
      id: "workspace",
      type: "workspace",
      name: assigneeOptions.workspace.name || "Empresa",
    });
  }
  for (const c of assigneeOptions?.clients || []) {
    list.push({
      id: Number(c.id),
      type: "client",
      name: c.name || `Cliente #${c.id}`,
    });
  }
  return list;
}

function portalAssigneeOptionKey(option) {
  if (!option) return "";
  if (option.type === "workspace" || option.id === "workspace") return "workspace";
  return `client:${Number(option.id)}`;
}

function PortalTaskDetailDialog({
  card,
  columns,
  assigneeOptions,
  onClose,
  onMove,
  onComment,
  onSaveMeta,
  onSaveAssignees,
  onAddAttachments,
  onDeleteAttachment,
  moveBusy,
  saving,
}) {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const commentFileInputRef = useRef(null);
  const [commentText, setCommentText] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editDueAt, setEditDueAt] = useState("");
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [savingAssignees, setSavingAssignees] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);

  const currentCol = (columns || []).find((c) => Number(c.id) === Number(card?.columnId));
  const movableColumns = (columns || []).filter((c) => c.clientCanMoveTo !== false && !c.isDone);
  const comments = card?.comments || [];
  const attachments = card?.attachments || [];
  const assignees = card?.assigneeUsers || [];
  const pickerOptions = useMemo(
    () => buildPortalAssigneeOptions(assigneeOptions),
    [assigneeOptions]
  );

  useEffect(() => {
    setCommentText("");
    setCommentFiles([]);
    setEditing(false);
    setEditTitle(card?.title || "");
    setEditDesc(card?.description || "");
    setEditDueAt(toDatetimeLocalValue(card?.dueAt));
  }, [card?.id, card?.title, card?.description, card?.dueAt]);

  useEffect(() => {
    const current = (card?.assigneeUsers || []).map((a) => {
      if (a.type === "workspace" || a.id === "workspace") {
        return (
          pickerOptions.find((o) => o.type === "workspace") || {
            id: "workspace",
            type: "workspace",
            name: a.name || "Empresa",
          }
        );
      }
      const id = Number(a.id);
      return (
        pickerOptions.find((o) => o.type === "client" && Number(o.id) === id) || {
          id,
          type: "client",
          name: a.name || `Cliente #${id}`,
        }
      );
    });
    setSelectedAssignees(current);
  }, [card?.id, card?.assigneeUsers, pickerOptions]);

  if (!card) return null;

  const overdue = !currentCol?.isDone && isOverdue(card.dueAt);
  const dueLabel = formatDueFull(card.dueAt);
  const canComment =
    Boolean(commentText.trim()) || commentFiles.length > 0;

  const handleUploadAttachments = async (incoming) => {
    const list = Array.isArray(incoming) ? incoming : [incoming];
    const files = list.filter(Boolean);
    if (!files.length || uploadingAttachments) return;
    setUploadingAttachments(true);
    try {
      await onAddAttachments(files);
    } finally {
      setUploadingAttachments(false);
    }
  };

  const submitComment = async () => {
    if (!canComment || saving) return;
    await onComment({
      body: commentText.trim(),
      files: commentFiles,
    });
    setCommentText("");
    setCommentFiles([]);
  };

  return (
    <Dialog open={Boolean(card)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6, pb: 1.25 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`#${card.id}`}
              variant="outlined"
              sx={{ fontWeight: 800, height: 24 }}
            />
            {currentCol ? (
              <Chip
                size="small"
                label={currentCol.name}
                sx={{
                  height: 24,
                  fontWeight: 700,
                  bgcolor: alpha(columnAccent(theme, currentCol), 0.12),
                  color: columnAccent(theme, currentCol),
                }}
              />
            ) : null}
            {card.hasInternalEscalation ? (
              <Chip
                size="small"
                icon={<SyncAltIcon />}
                label="Em tratamento interno"
                color="info"
                sx={{ height: 24, fontWeight: 700 }}
              />
            ) : null}
            {currentCol?.isDone ? (
              <Chip
                size="small"
                icon={<CheckCircleOutlineIcon />}
                label="Concluída"
                color="success"
                sx={{ height: 24, fontWeight: 700 }}
              />
            ) : null}
          </Stack>
          {!editing ? (
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.3, pr: 2 }}>
              {card.title}
            </Typography>
          ) : null}
        </Stack>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: alpha(theme.palette.primary.main, 0.015) }}>
        {card.hasInternalEscalation ? (
          <Alert
            severity="info"
            icon={<SupportAgentOutlinedIcon />}
            sx={{ mb: 2, borderRadius: 2 }}
          >
            Nossa equipe já está tratando esta demanda internamente. Você acompanha o status e
            conversa por aqui — os detalhes operacionais permanecem só com a equipe.
          </Alert>
        ) : null}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          <Chip
            icon={<ScheduleOutlinedIcon />}
            label={dueLabel ? `Prazo: ${dueLabel}` : "Sem prazo"}
            color={overdue ? "error" : "default"}
            variant={overdue || dueLabel ? "filled" : "outlined"}
            sx={{ fontWeight: 700 }}
          />
          <Chip
            icon={<GroupOutlinedIcon />}
            label={
              assignees.length
                ? `${assignees.length} responsável${assignees.length > 1 ? "eis" : ""}`
                : "Sem responsáveis"
            }
            color={assignees.length ? "primary" : "default"}
            variant={assignees.length ? "filled" : "outlined"}
            sx={{ fontWeight: 700 }}
          />
          <Chip
            icon={<AttachFileOutlinedIcon />}
            label={`${attachments.length} anexo${attachments.length === 1 ? "" : "s"}`}
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
          <Chip
            icon={<ChatBubbleOutlineIcon />}
            label={`${comments.length} comentário${comments.length === 1 ? "" : "s"}`}
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        {editing ? (
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              label="Título"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              fullWidth
            />
            <TextField
              label="Descrição"
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Prazo"
              type="datetime-local"
              value={editDueAt}
              onChange={(e) => setEditDueAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Opcional — ajuda a priorizar o que vence primeiro"
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                disabled={saving || !editTitle.trim()}
                onClick={async () => {
                  const ok = await onSaveMeta({
                    title: editTitle.trim(),
                    description: editDesc.trim() || null,
                    dueAt: editDueAt || null,
                  });
                  if (ok !== false) setEditing(false);
                }}
              >
                Salvar
              </Button>
              <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
                Cancelar
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Box sx={{ mb: 2.25 }}>
            {card.description ? (
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                {card.description}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Sem descrição adicional.
              </Typography>
            )}
            <Button size="small" sx={{ mt: 1, textTransform: "none" }} onClick={() => setEditing(true)}>
              Editar título, descrição e prazo
            </Button>
          </Box>
        )}

        <Box sx={{ mb: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <GroupOutlinedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={800}>
              Responsáveis
            </Typography>
          </Stack>
          <Autocomplete
            multiple
            options={pickerOptions}
            value={selectedAssignees}
            onChange={async (_, next) => {
              setSelectedAssignees(next);
              if (!onSaveAssignees) return;
              setSavingAssignees(true);
              try {
                await onSaveAssignees(next);
              } finally {
                setSavingAssignees(false);
              }
            }}
            getOptionLabel={(o) => o?.name || String(o?.id || "")}
            isOptionEqualToValue={(a, b) =>
              portalAssigneeOptionKey(a) === portalAssigneeOptionKey(b)
            }
            disabled={saving || savingAssignees || !pickerOptions.length}
            size="small"
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={portalAssigneeOptionKey(option)}
                  avatar={
                    <Avatar>{getInitials(option.name || "?")}</Avatar>
                  }
                  label={option.name}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  pickerOptions.length
                    ? "Empresa ou contatos vinculados"
                    : "Nenhuma opção disponível"
                }
                helperText="Você pode atribuir a empresa ou os cadastros PF vinculados a este cliente."
              />
            )}
          />
          {!assignees.length && !selectedAssignees.length ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              Ainda sem responsáveis nesta tarefa.
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ mb: 2.75 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
            <AttachFileOutlinedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={800}>
              Anexos
            </Typography>
          </Stack>

          <FileDropHandler
            onFileUpload={handleUploadAttachments}
            dropLabel="Solte arquivos para anexar à tarefa"
          >
            <Paper
              variant="outlined"
              sx={{
                p: 1.75,
                mb: 1.5,
                borderStyle: "dashed",
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              {uploadingAttachments ? <LinearProgress sx={{ mb: 1.25, borderRadius: 1 }} /> : null}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <CloudUploadOutlinedIcon color="action" />
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      Arraste arquivos aqui ou clique para adicionar
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PDFs, imagens e documentos ficam disponíveis para a equipe
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={uploadingAttachments || saving}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Adicionar anexo
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  multiple
                  onChange={(e) => {
                    const selected = Array.from(e.target.files || []);
                    if (selected.length) handleUploadAttachments(selected);
                    e.target.value = "";
                  }}
                />
              </Stack>
            </Paper>
          </FileDropHandler>

          {attachments.length ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fill, minmax(160px, 1fr))",
                },
                gap: 1.25,
              }}
            >
              {attachments.map((att) => (
                <PortalAttachmentTile
                  key={att.id}
                  att={att}
                  deleting={deletingAttachmentId === att.id}
                  onDelete={async (id) => {
                    setDeletingAttachmentId(id);
                    try {
                      await onDeleteAttachment(id);
                    } finally {
                      setDeletingAttachmentId(null);
                    }
                  }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nenhum anexo ainda.
            </Typography>
          )}
        </Box>

        {!currentCol?.isDone ? (
          <Box sx={{ mb: 2.75 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <HourglassTopOutlinedIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={800}>
                Atualizar status
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.25 }}>
              Arraste o card no quadro ou use os botões abaixo. “Aguardando equipe” pede nossa ação;
              “Aguardando cliente” deixa a bola com você.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {movableColumns.map((col) => {
                const active = Number(col.id) === Number(card.columnId);
                return (
                  <Button
                    key={col.id}
                    size="small"
                    variant={active ? "contained" : "outlined"}
                    disabled={moveBusy || active}
                    onClick={() => onMove(card.id, col.id)}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                  >
                    {col.name}
                  </Button>
                );
              })}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              A conclusão fica com a equipe, para garantir o fechamento correto.
            </Typography>
          </Box>
        ) : (
          <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>
            Esta tarefa foi concluída. Se ainda precisar de algo, comente abaixo ou abra uma nova
            tarefa.
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <ChatBubbleOutlineIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={800}>
            Conversa
          </Typography>
          <Chip size="small" label={comments.length} sx={{ height: 20, fontWeight: 700 }} />
        </Stack>

        <Stack spacing={1.25} sx={{ mb: 2, maxHeight: 300, overflow: "auto", pr: 0.5 }}>
          {comments.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderStyle: "dashed",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Ainda não há mensagens. Envie texto ou anexos para falar com a equipe sobre esta
                tarefa.
              </Typography>
            </Paper>
          ) : (
            comments.map((c) => {
              const isClient = Boolean(c.authorIsClient || !c.authorName);
              const showBody =
                c.body &&
                !(c.hasMedia && c.fileName && String(c.body).trim() === String(c.fileName).trim());
              return (
                <Stack
                  key={c.id}
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                  sx={{
                    flexDirection: isClient ? "row-reverse" : "row",
                  }}
                >
                  <Avatar
                    src={!isClient ? c.authorAvatarUrl || undefined : undefined}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: isClient
                        ? alpha(theme.palette.primary.main, 0.15)
                        : alpha(theme.palette.info.main, 0.15),
                      color: isClient ? "primary.main" : "info.main",
                    }}
                  >
                    {isClient ? (
                      <PersonOutlineIcon fontSize="small" />
                    ) : (
                      <SupportAgentOutlinedIcon fontSize="small" />
                    )}
                  </Avatar>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.35,
                      borderRadius: 2,
                      maxWidth: "85%",
                      bgcolor: isClient
                        ? alpha(theme.palette.primary.main, 0.08)
                        : theme.palette.background.paper,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.35 }}>
                      <Typography variant="caption" fontWeight={800}>
                        {isClient ? "Você" : c.authorName || "Equipe"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCommentTime(c.createdAt)}
                      </Typography>
                    </Stack>
                    {showBody ? (
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                        {c.body}
                      </Typography>
                    ) : null}
                    <CommentMedia comment={c} />
                  </Paper>
                </Stack>
              );
            })
          )}
        </Stack>

        <PendingFilesList
          files={commentFiles}
          onRemove={(index) => setCommentFiles((prev) => prev.filter((_, i) => i !== index))}
        />

        <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mt: commentFiles.length ? 1 : 0 }}>
          <Tooltip title="Anexar na conversa">
            <span>
              <IconButton
                onClick={() => commentFileInputRef.current?.click()}
                disabled={saving}
                aria-label="Anexar arquivo na conversa"
              >
                <AttachFileOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <input
            ref={commentFileInputRef}
            type="file"
            hidden
            multiple
            onChange={(e) => {
              const selected = Array.from(e.target.files || []);
              if (selected.length) setCommentFiles((prev) => [...prev, ...selected]);
              e.target.value = "";
            }}
          />
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            placeholder="Escreva uma mensagem ou anexe um arquivo…"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                await submitComment();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ alignSelf: "flex-end", mb: 0.5 }}>
                  <Tooltip title="Enviar (Ctrl/⌘ + Enter)">
                    <span>
                      <IconButton
                        color="primary"
                        onClick={submitComment}
                        disabled={saving || !canComment}
                        aria-label="Enviar comentário"
                      >
                        <SendIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

async function uploadPortalFiles(files, prefix) {
  const uploaded = [];
  for (const file of files || []) {
    if (!file) continue;
    const result = await portalUploadOne(file, { prefix });
    uploaded.push({
      fileName: file.name,
      fileUrl: result.url,
      fileKey: result.key || null,
      mimeType: file.type || null,
      sizeBytes: file.size,
      messageType: inferPortalFileMessageType(file),
    });
  }
  return uploaded;
}

export default function ClientPortalTasksPage() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [board, setBoard] = useState(null);
  const [columns, setColumns] = useState([]);
  const [cards, setCards] = useState([]);
  const [assigneeOptions, setAssigneeOptions] = useState({ workspace: null, clients: [] });
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [moveBusy, setMoveBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await portalGetTasksBoard();
      setBoard(data?.board || null);
      setColumns(data?.columns || []);
      setCards(data?.cards || []);
      setAssigneeOptions(
        data?.assigneeOptions || {
          workspace: data?.board?.workspaceName
            ? { id: "workspace", type: "workspace", name: data.board.workspaceName }
            : null,
          clients: [],
        }
      );
    } catch (err) {
      setError(
        getClientPortalErrorMessage(err, {
          fallback: "Não foi possível carregar suas tarefas agora.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cardsByColumn = useMemo(() => groupCardsByColumn(columns, cards), [columns, cards]);
  const openCount = useMemo(
    () =>
      cards.filter((c) => {
        const col = columns.find((colItem) => Number(colItem.id) === Number(c.columnId));
        return !col?.isDone && !c.completedAt;
      }).length,
    [cards, columns]
  );
  const selectedLive = useMemo(() => {
    if (!selected) return null;
    return cards.find((c) => Number(c.id) === Number(selected.id)) || selected;
  }, [cards, selected]);

  const defaultCreateColumnId = useMemo(() => {
    const firstOpen = (columns || []).find((c) => !c.isDone && c.clientCanMoveTo !== false);
    return firstOpen?.id || columns?.[0]?.id || null;
  }, [columns]);

  const applyBoardPayload = (data) => {
    if (data?.cards) setCards(data.cards);
    if (data?.columns) setColumns(data.columns);
    if (data?.card) setSelected(data.card);
  };

  const handleCreate = async ({ title, description, dueAt, columnId, files = [] }) => {
    if (!title?.trim()) return;
    setSaving(true);
    try {
      let attachments = [];
      if (files.length) {
        attachments = await uploadPortalFiles(
          files,
          `portal-cliente/tasks/${board?.id || "board"}`
        );
      }
      const data = await portalCreateTaskCard({
        title: title.trim(),
        description: description || null,
        dueAt: dueAt || null,
        columnId: columnId || defaultCreateColumnId,
        attachments,
      });
      applyBoardPayload(data);
      setCreateOpen(false);
    } catch (err) {
      setError(
        getClientPortalErrorMessage(err, { fallback: "Não foi possível criar a tarefa." })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleQuickAdd = async (columnId, title) => {
    setSaving(true);
    try {
      const data = await portalCreateTaskCard({ title, columnId });
      applyBoardPayload(data);
      return true;
    } catch (err) {
      setError(
        getClientPortalErrorMessage(err, { fallback: "Não foi possível criar a tarefa." })
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleMove = useCallback(async (cardId, columnId, position) => {
    setMoveBusy(true);
    let snapshot = null;
    setCards((prev) => {
      snapshot = prev;
      const moving = prev.find((c) => Number(c.id) === Number(cardId));
      if (!moving) return prev;
      const without = prev.filter((c) => Number(c.id) !== Number(cardId));
      const destCards = without
        .filter((c) => Number(c.columnId) === Number(columnId))
        .sort((a, b) => (a.position || 0) - (b.position || 0) || a.id - b.id);
      const insertAt =
        position == null || position < 0
          ? destCards.length
          : Math.min(position, destCards.length);
      const nextDest = [...destCards];
      nextDest.splice(insertAt, 0, { ...moving, columnId: Number(columnId) });
      const reindexedDest = nextDest.map((c, index) => ({ ...c, position: index }));
      const other = without.filter((c) => Number(c.columnId) !== Number(columnId));
      return [...other, ...reindexedDest];
    });

    try {
      const data = await portalMoveTaskCard(cardId, {
        columnId,
        position: position != null ? Number(position) : undefined,
      });
      applyBoardPayload(data);
    } catch (err) {
      if (snapshot) setCards(snapshot);
      setError(
        getClientPortalErrorMessage(err, {
          fallback: "Não foi possível atualizar o status da tarefa.",
        })
      );
    } finally {
      setMoveBusy(false);
    }
  }, []);

  const onDragEnd = useCallback(
    (result) => {
      const { destination, source, draggableId } = result;
      if (!destination) return;
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      const destColumnId = Number(destination.droppableId);
      const destColumn = (columns || []).find((c) => Number(c.id) === destColumnId);
      if (!destColumn || destColumn.isDone || destColumn.clientCanMoveTo === false) {
        setError("Não é possível mover para esta coluna pelo portal.");
        return;
      }

      const cardId = Number(draggableId);
      if (!Number.isFinite(cardId) || cardId <= 0) return;
      void handleMove(cardId, destColumnId, destination.index);
    },
    [columns, handleMove]
  );

  const handleComment = async ({ body, files = [] }) => {
    if (!selectedLive?.id) return;
    if (!body?.trim() && !files.length) return;
    setSaving(true);
    try {
      let lastData = null;
      if (files.length) {
        const uploaded = await uploadPortalFiles(
          files,
          `portal-cliente/tasks/${board?.id || "board"}/${selectedLive.id}`
        );
        for (const file of uploaded) {
          lastData = await portalCreateTaskComment(selectedLive.id, {
            body: body || "",
            fileUrl: file.fileUrl,
            fileName: file.fileName,
            fileKey: file.fileKey,
            mimeType: file.mimeType,
            sizeBytes: file.sizeBytes,
            messageType: file.messageType,
          });
          body = "";
        }
      } else {
        lastData = await portalCreateTaskComment(selectedLive.id, { body });
      }

      if (lastData?.cards) {
        applyBoardPayload(lastData);
      } else if (lastData?.comments) {
        setCards((prev) =>
          prev.map((c) =>
            Number(c.id) === Number(selectedLive.id)
              ? {
                  ...c,
                  comments: lastData.comments,
                  attachments: lastData.card?.attachments || c.attachments,
                }
              : c
          )
        );
        setSelected((prev) =>
          prev && Number(prev.id) === Number(selectedLive.id)
            ? {
                ...prev,
                comments: lastData.comments,
                attachments: lastData.card?.attachments || prev.attachments,
              }
            : prev
        );
      } else {
        await load();
      }
    } catch (err) {
      setError(
        getClientPortalErrorMessage(err, { fallback: "Não foi possível enviar a mensagem." })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMeta = async ({ title, description, dueAt }) => {
    if (!selectedLive?.id) return false;
    setSaving(true);
    try {
      const data = await portalUpdateTaskCard(selectedLive.id, {
        title,
        description,
        dueAt: dueAt === undefined ? undefined : dueAt || null,
      });
      applyBoardPayload(data);
      return true;
    } catch (err) {
      setError(
        getClientPortalErrorMessage(err, { fallback: "Não foi possível salvar as alterações." })
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAssignees = async (nextAssignees = []) => {
    if (!selectedLive?.id) return false;
    try {
      const data = await portalUpdateTaskCard(selectedLive.id, {
        assignees: (nextAssignees || []).map((a) =>
          a.type === "workspace" || a.id === "workspace"
            ? { type: "workspace", id: "workspace" }
            : { type: "client", id: Number(a.id) }
        ),
      });
      applyBoardPayload(data);
      return true;
    } catch (err) {
      setError(
        getClientPortalErrorMessage(err, {
          fallback: "Não foi possível atualizar os responsáveis.",
        })
      );
      return false;
    }
  };

  const handleAddAttachments = async (files) => {
    if (!selectedLive?.id || !files?.length) return;
    const uploaded = await uploadPortalFiles(
      files,
      `portal-cliente/tasks/${board?.id || "board"}/${selectedLive.id}`
    );
    let lastData = null;
    for (const file of uploaded) {
      lastData = await portalCreateTaskAttachment(selectedLive.id, file);
    }
    if (lastData) applyBoardPayload(lastData);
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!selectedLive?.id) return;
    try {
      const data = await portalDeleteTaskAttachment(selectedLive.id, attachmentId);
      applyBoardPayload(data);
    } catch (err) {
      setError(
        getClientPortalErrorMessage(err, { fallback: "Não foi possível remover o anexo." })
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 10, width: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasAnyCards = cards.length > 0;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        pb: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
            theme.palette.background.paper,
            1
          )} 48%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          spacing={1.75}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="h5" fontWeight={900} sx={{ letterSpacing: -0.4 }}>
                Suas tarefas
              </Typography>
              <Chip
                size="small"
                label={`${openCount} em aberto`}
                color="primary"
                sx={{ fontWeight: 800, height: 24 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Acompanhe prazos, responsáveis e anexos. Converse com a equipe — o trabalho interno
              fica conosco.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              alignSelf: { xs: "stretch", sm: "auto" },
              fontWeight: 800,
              px: 2.25,
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            Nova tarefa
          </Button>
        </Stack>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {!hasAnyCards ? (
        <Paper
          variant="outlined"
          sx={{
            py: 6,
            px: 3,
            mb: 2.5,
            textAlign: "center",
            borderRadius: 3,
            borderStyle: "dashed",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              mx: "auto",
              mb: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: "primary.main",
            }}
          >
            <AssignmentOutlinedIcon />
          </Box>
          <Typography variant="subtitle1" fontWeight={800} gutterBottom>
            Nenhuma tarefa por enquanto
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto", mb: 2.5 }}>
            Crie a primeira com prazo e anexos, se precisar — a equipe acompanha pelo painel.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Criar primeira tarefa
          </Button>
        </Paper>
      ) : null}

      <DragDropContext onDragEnd={onDragEnd}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            overflowY: "hidden",
            pb: 1.5,
            alignItems: "flex-start",
            width: "100%",
            minWidth: 0,
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { height: 8 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.text.primary, 0.18),
              borderRadius: 999,
            },
          }}
        >
          {(columns || []).map((col) => {
            const colCards = cardsByColumn.get(Number(col.id)) || [];
            const canAdd = !col.isDone && col.clientCanMoveTo !== false;
            const canDrop = !col.isDone && col.clientCanMoveTo !== false;
            return (
              <PortalTaskColumn
                key={col.id}
                column={col}
                cards={colCards}
                onOpenCard={setSelected}
                onQuickAdd={handleQuickAdd}
                canAdd={canAdd}
                canDrop={canDrop}
              />
            );
          })}
        </Box>
      </DragDropContext>

      <PortalCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        saving={saving}
        defaultColumnId={defaultCreateColumnId}
      />

      <PortalTaskDetailDialog
        card={selectedLive}
        columns={columns}
        assigneeOptions={assigneeOptions}
        onClose={() => setSelected(null)}
        onMove={handleMove}
        onComment={handleComment}
        onSaveMeta={handleSaveMeta}
        onSaveAssignees={handleSaveAssignees}
        onAddAttachments={handleAddAttachments}
        onDeleteAttachment={handleDeleteAttachment}
        moveBusy={moveBusy}
        saving={saving}
      />
    </Box>
  );
}
